import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { fetchPositions, keys } from "@/lib/queries";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Select } from "@/components/ui/Select";
import { Pagination } from "@/components/ui/Pagination";
import { SortableHeader } from "@/components/ui/SortableHeader";
import { useTableState } from "@/lib/useTableState";
import { fmtQty, fmtRelative, statusLabel } from "@/lib/format";
import { CurrencyAmount } from "@/components/ui/CurrencyAmount";
import {
  RealizedPnLAmount,
  debitLeg,
  payoutLeg,
} from "@/components/activity/amountCurrency";
import { cn } from "@/lib/cn";
import { RedemptionDetailModal } from "@/components/activity/RedemptionDetailModal";
import { CurrencyChip } from "@/components/currency/columns";
import {
  ActivityScopeBar,
  ResetScopeButton,
  useActivityScope,
  useEventMarketScope,
  validateActivityScopeSearch,
  EMPTY_SCOPE,
  type ActivityScopeSearch,
} from "@/components/activity/ActivityScopeBar";
import {
  MarketCell,
  ModeCurrencyCell,
  OutcomeCell,
  StateRow,
  Th,
  UserCell,
} from "@/components/activity/ActivityCells";
import { useScopedPage } from "@/components/activity/useScopedPage";
import { useCurrencySearch } from "@/components/activity/useCurrencySearch";
import {
  NON_STAKE_CURRENCY_HINT,
  StakeCurrencyFilter,
  parseStakeCurrency,
  type StakeCurrency,
} from "@/components/currency/StakeCurrencyFilter";

/**
 * The shared Event / Market / User scope, plus this page's own currency
 * filter (SM-490) — the same control and the same `?currency` key the
 * other three Activity lists took in SM-511.
 */
export const Route = createFileRoute("/_app/positions")({
  validateSearch: (
    raw: Record<string, unknown>,
  ): ActivityScopeSearch & { currency?: StakeCurrency } => ({
    ...validateActivityScopeSearch(raw),
    currency: parseStakeCurrency(raw.currency),
  }),
  component: ActivePositionsPage,
});

interface PositionFilters {
  status: string;
  direction: string;
}

// The settlement-side statuses a position can actually hold — the
// domain.PositionStatus set, nothing invented. OPEN is live exposure;
// the rest are what a resolved market leaves behind. The upstream
// default (no ?status) is OPEN-only, which is what this page is for;
// the others let an operator follow one holder past resolution without
// leaving the page.
//
// RESOLVED is legacy — written only by the pre-redeem auto-payout flow —
// so it sits last rather than beside the statuses new writes produce.
const POSITION_STATUSES = [
  { value: "OPEN", label: "Open" },
  { value: "REDEEMABLE", label: "Redeemable" },
  { value: "REDEEMED", label: "Redeemed" },
  { value: "LOST", label: "Lost" },
  { value: "REFUNDED", label: "Refunded" },
  { value: "CLOSED", label: "Closed" },
  { value: "RESOLVED", label: "Resolved (legacy)" },
];

function ActivePositionsPage() {
  const table = useTableState<PositionFilters>({
    defaultSort: { by: "updated_at", dir: "desc" },
    defaultFilters: { status: "", direction: "" },
  });
  const { scope, setScope, resetKey, hasScope } = useActivityScope();
  const [currency, setCurrency] = useCurrencySearch(parseStakeCurrency);
  // The redemption drill-down needs the position's PAYOUT leg as well as
  // its id: a redemption pays GREEN in GAME, and the attempts endpoint
  // carries no currency of its own (SM-549).
  const [inspecting, setInspecting] = useState<{
    positionID: string;
    currency: string;
  } | null>(null);
  const marketScope = useEventMarketScope(scope);
  // The currency joins the scope's reset key: it narrows the result set
  // the same way, so a page-5 operator who picks a currency must land
  // back on page 1 rather than past the end of a shorter list.
  useScopedPage(`${resetKey}|${currency ?? ""}`, table.setPage);

  const params = {
    status: table.filters.status || undefined,
    direction: table.filters.direction || undefined,
    market_id: marketScope.marketIDs,
    user_id: scope.userId || undefined,
    currency,
  };
  const positions = useQuery({
    queryKey: keys.positionsList(table.limit, table.offset, table.sort, params),
    queryFn: () =>
      fetchPositions({
        limit: table.limit,
        offset: table.offset,
        sort: table.sort,
        ...params,
      }),
    enabled: !marketScope.pending,
  });

  const filtered = hasScope || table.hasActiveFilters || Boolean(currency);
  // Payout columns only mean something after resolution, and the
  // default view is open exposure — a Gross/Fee/Net trio that is "—" on
  // every row is noise. Shown when the operator has narrowed to a
  // settled status, which is also when they are answering a support
  // question rather than watching risk.
  const settledView =
    table.filters.status === "REDEEMED" || table.filters.status === "REDEEMABLE";
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Active positions</h1>
        <p className="text-sm text-muted">
          Current exposure across all users and markets. Defaults to open
          positions; widen the status filter to follow a holder past resolution.
        </p>
      </div>
      <ActivityScopeBar scope={scope} onChange={setScope}>
        <div className="w-44">
          <Select
            label="Status"
            placeholder="Open only"
            value={table.filters.status}
            onChange={(e) => table.setFilter("status", e.target.value)}
            options={POSITION_STATUSES}
          />
        </div>
        <div className="w-52">
          <StakeCurrencyFilter value={currency} onChange={setCurrency} />
        </div>
        <div className="w-40">
          <Select
            label="Direction"
            placeholder="Any"
            value={table.filters.direction}
            onChange={(e) => table.setFilter("direction", e.target.value)}
            options={[
              { value: "LONG", label: "Long" },
              { value: "SHORT", label: "Short" },
            ]}
          />
        </div>
        {/* basis-full: its own line under the bar, so the two-line
            note does not drag the controls beside it out of alignment. */}
        <p className="basis-full text-2xs text-t-4">{NON_STAKE_CURRENCY_HINT}</p>
        <ResetScopeButton
          show={filtered}
          onReset={() => {
            table.reset();
            setCurrency(undefined);
            setScope(EMPTY_SCOPE);
          }}
        />
      </ActivityScopeBar>
      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Positions</CardTitle>
          <span className="text-2xs text-muted">{positions.data?.page.total ?? 0}</span>
        </CardHeader>
        <CardBody className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-surface-2">
                <tr>
                  <Th className="px-5">Market</Th>
                  {/* SM-469: which book and which currency. Never merged
                      into the market column — they are separate facts. */}
                  <Th>Mode · currency</Th>
                  <Th>User</Th>
                  <Th>Outcome</Th>
                  <Th>Direction</Th>
                  <SortableHeader label="Qty" field="quantity" sort={table.sort} onSortChange={table.setSort} align="right" />
                  <Th align="right">Avg</Th>
                  <SortableHeader label="Realized PnL" field="realized_pnl" sort={table.sort} onSortChange={table.setSort} align="right" />
                  <Th>Status</Th>
                  {settledView && <Th align="right">Payout</Th>}
                  {settledView && <Th align="right">Received</Th>}
                  <SortableHeader label="Updated" field="updated_at" sort={table.sort} onSortChange={table.setSort} align="right" className="px-5" />
                </tr>
              </thead>
              <tbody>
                {(positions.data?.items ?? []).map((p) => (
                  <tr key={p.position_id} className="border-b border-border/60 last:border-0">
                    <MarketCell row={p} />
                    <ModeCurrencyCell row={p} />
                    <UserCell row={p} />
                    <OutcomeCell row={p} />
                    <td className="px-3 py-2.5">
                      <span
                        className={cn(
                          p.direction === "LONG" ? "text-bid" : "text-ask",
                          "text-xs font-medium",
                        )}
                      >
                        {p.direction}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      {fmtQty(p.quantity, p.amount_precision)}
                    </td>
                    {/* avg_price is intent space already — settlement
                        books positions from the intent fill price. */}
                    <td className="px-3 py-2.5 text-right font-mono">
                      {/* The cost basis is the STAKE leg — RED in GAME
                          (SM-549), which is also the leg the currency
                          filter above narrows on. */}
                      <CurrencyAmount
                        currency={debitLeg(p)}
                        value={p.avg_price}
                        trimZeros
                      />
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      {/* Realized P&L spans both legs in GAME (GREEN
                          proceeds less RED basis), so it carries no mark
                          there — see amountCurrency.tsx. */}
                      <RealizedPnLAmount row={p} value={p.realized_pnl} />
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge tone={p.status === "OPEN" ? "success" : "muted"}>
                        {statusLabel(p.status)}
                      </Badge>
                    </td>
                    {settledView && (
                      <td className="px-3 py-2.5 text-right font-mono">
                        {/* The gross the position resolved at. Present
                            on REDEEMABLE rows; a REDEEMED row has
                            already spent it, so the settlement feed no
                            longer stamps it. */}
                        {p.redeemable_payout ? (
                          <CurrencyAmount
                            currency={payoutLeg(p)}
                            value={p.redeemable_payout}
                            tone="muted"
                          />
                        ) : (
                          <span className="text-2xs text-muted">—</span>
                        )}
                      </td>
                    )}
                    {settledView && (
                      <td className="px-3 py-2.5 text-right">
                        {p.status === "REDEEMED" ? (
                          // What the user actually received is per
                          // ATTEMPT, not per position — a redemption can
                          // fail and be retried — so the row links to
                          // the attempts rather than inventing a single
                          // number for a thing that may have several.
                          <button
                            type="button"
                            onClick={() =>
                              setInspecting({
                                positionID: p.position_id,
                                currency: payoutLeg(p),
                              })
                            }
                            className="text-xs font-medium text-accent hover:underline"
                          >
                            View attempts
                          </button>
                        ) : (
                          <span className="text-2xs text-muted">not claimed</span>
                        )}
                      </td>
                    )}
                    <td className="px-5 py-2.5 text-right text-2xs text-muted">
                      {fmtRelative(p.updated_at)}
                    </td>
                  </tr>
                ))}
                {(positions.isLoading || marketScope.pending) && (
                  <StateRow colSpan={settledView ? 12 : 10}>Loading…</StateRow>
                )}
                {positions.isError && (
                  <StateRow colSpan={settledView ? 12 : 10}>Could not load positions. Retry in a moment.</StateRow>
                )}
                {positions.data && positions.data.items.length === 0 && (
                  <StateRow colSpan={settledView ? 12 : 10}>
                    {/* Name the currency when one is picked: "no
                        positions for these filters" beside a Currency
                        control reads as a broken filter, while "no
                        positions staked in RED" is an answer. */}
                    {currency ? (
                      <>
                        No positions staked in <CurrencyChip currency={currency} />
                        {hasScope || table.hasActiveFilters ? " for these filters" : ""}.
                      </>
                    ) : (
                      <>No positions{filtered ? " for these filters" : ""}.</>
                    )}
                  </StateRow>
                )}
              </tbody>
            </table>
          </div>
          {positions.data && (
            <Pagination
              page={positions.data.page}
              currentPage={table.page}
              onPageChange={table.setPage}
              itemLabel="positions"
            />
          )}
        </CardBody>
      </Card>
      {inspecting && (
        <RedemptionDetailModal
          positionID={inspecting.positionID}
          currency={inspecting.currency}
          onClose={() => setInspecting(null)}
        />
      )}
    </div>
  );
}
